import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Slider,
} from '@mui/material';
import {
  GANN_CONFLUENCE_ORB, GANN_CONFLUENCE_MAX_COMBINED_ORB, ORB_BOUNDS, COMBINED_BOUNDS,
} from '../utils/gannConfluenceOverlay';

/**
 * The two dials on the Gann confluence scan, asked for before it runs.
 *
 * <h2>Why this exists</h2>
 *
 * The condition on its own is not selective. Measured over 2020-2024 with twelve bodies at an orb
 * of 7, <b>98.4% of days qualify</b> — 1797 of 1827 — because 66 body pairs and a Moon crossing
 * every angle each month make some trine and some hard aspect share a body almost always. So the
 * useful question is not "does this happen" but "how tight does it have to be before it means
 * something", and that is a question to experiment with rather than one to answer once in a
 * constant.
 *
 * <p>Two separate dials, because they narrow different things and the reader will want to move
 * them independently:
 *
 * <ul>
 *   <li><b>Orb</b> — how far from exact either aspect may sit to count at all. Widening it finds
 *       more configurations.</li>
 *   <li><b>Combined</b> — the ceiling on the two orbs ADDED, for a day to be drawn. This is the
 *       selectivity dial, and it is the one that turns the scan from a highlighter into a
 *       marker.</li>
 * </ul>
 */
export default function GannScanDialog({ open, symbol, onRun, onClose }) {
  const [orb, setOrb] = useState(GANN_CONFLUENCE_ORB);
  const [combined, setCombined] = useState(GANN_CONFLUENCE_MAX_COMBINED_ORB);

  // Reset to the defaults each time it opens rather than remembering the last run. The numbers are
  // an experiment, and reopening the dialog is where a reader re-reads what the defaults were.
  useEffect(() => {
    if (!open) return;
    setOrb(GANN_CONFLUENCE_ORB);
    setCombined(GANN_CONFLUENCE_MAX_COMBINED_ORB);
  }, [open]);

  // The combined ceiling cannot usefully exceed twice the orb: neither aspect can be further out
  // than the orb, so their sum is bounded by 2x it, and anything above that is the same as no
  // ceiling at all while looking like a setting.
  const combinedMax = Math.min(COMBINED_BOUNDS.max, orb * 2);
  const effectiveCombined = Math.min(combined, combinedMax);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Gann Confluence Scan
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {symbol} · chart start to a year past the last bar
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Marks every day where a trine and a conjunction, opposition or square share a planet.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Orb — how far from exact either aspect may sit
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              size="small"
              value={orb}
              min={ORB_BOUNDS.min}
              max={ORB_BOUNDS.max}
              step={0.5}
              marks={[{ value: 1, label: '1°' }, { value: 7, label: '7°' }, { value: 15, label: '15°' }]}
              onChange={(_, v) => setOrb(v)}
              valueLabelDisplay="auto"
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              value={orb}
              onChange={(e) => setOrb(Number(e.target.value))}
              onBlur={() => setOrb(clamp(orb, ORB_BOUNDS))}
              inputProps={{ min: ORB_BOUNDS.min, max: ORB_BOUNDS.max, step: 0.5, 'aria-label': 'Gann scan orb' }}
              sx={{ width: 90 }}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Combined — the two orbs added, for a day to be drawn
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              size="small"
              value={effectiveCombined}
              min={COMBINED_BOUNDS.min}
              max={combinedMax}
              step={0.25}
              onChange={(_, v) => setCombined(v)}
              valueLabelDisplay="auto"
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              value={effectiveCombined}
              onChange={(e) => setCombined(Number(e.target.value))}
              onBlur={() => setCombined(clamp(combined, { min: COMBINED_BOUNDS.min, max: combinedMax }))}
              inputProps={{ min: COMBINED_BOUNDS.min, max: combinedMax, step: 0.25, 'aria-label': 'Gann scan combined orb' }}
              sx={{ width: 90 }}
            />
          </Box>
          {/* The measurement, so the dial arrives with a sense of scale rather than as a bare
              number. These are the figures the default was chosen from. */}
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
            Measured on 2020–2024 at orb 7: no ceiling marks 98.4% of days, 3° marks 45.7%,
            2° marks 22.8%, 1° marks 6.6%, 0.5° marks 1.7%.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onRun({
            orb: clamp(orb, ORB_BOUNDS),
            maxCombinedOrb: clamp(effectiveCombined, { min: COMBINED_BOUNDS.min, max: combinedMax }),
          })}
        >
          Scan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Forced into range; a non-numeric entry falls back to the low end rather than to NaN. */
function clamp(value, bounds) {
  const n = Number(value);
  if (!Number.isFinite(n)) return bounds.min;
  return Math.min(bounds.max, Math.max(bounds.min, n));
}

GannScanDialog.propTypes = {
  open: PropTypes.bool,
  symbol: PropTypes.string,
  /** ({orb, maxCombinedOrb}) */
  onRun: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
